import { LRUCache } from "../../../base/common/map.js";
import { select } from "three/src/nodes/TSL.js";
import { describeObject } from "../../../base/common/describeObject.js";
import { runWhenGlobalIdle, ThrottledDelayer } from "../../../base/common/async.js";
import { IAttachment } from "../../../base/common/attachment.js";
import { CancellationToken } from "../../../base/common/cancellation.js";
import { Emitter } from "../../../base/common/events.js";
import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { CommandsRegistry, ICommandService } from "../../commands/common/commands.js";
import { IConfigurationService } from "../../configuration/common/configuration.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";
import { IInstantiationService } from "../../instantiation/common/instantiation.js";
import { ILogService } from "../../log/common/log.js";
import { IStorageService } from "../../storage/common/storage.js";
import { isHierarchyNode as isHierarchyNode, IHierarchyNode, NodeFlags, NodeChangedEvent as HierarchyNodeChangedEvent, IHierarchyService, ITreeProviderService, HierarchyChangedEvent, SelectedNodesChangedEvent } from "./hierarchy.js";
import { DeleteNodeCommand, DeleteSelectedNodesCommand, DuplicateSelectedNodesCommand, NavigateHierarchyCommand, RenameNodeCommand, SelectNodeCommand, SetNodeExpandedCommand } from "./treeCommands.js";
import { getNodeId } from "@needle-tools/addressing";
import { flattenVisibleHierarchy } from "./visibleHierarchyRows.js";

// Monotonic counter bumped on EVERY write to a node's `selected` flag (the setter
// AND setWithoutNotify). getSelectedNodes() caches its result against this, so it
// re-walks the tree only when the selection actually changed — surviving silent
// mutation (setWithoutNotify fires no event) without any per-call walk. Module scope
// because HierarchyNode and HierarchyService share this file; over-bumping (e.g.
// deselecting an already-unselected node) only costs one extra walk, never staleness.
let _selectionEpoch = 0;

/** the node's PERSISTENT identity, or null — owners without userData (foreign
 * hosts, non-graph objects) simply never index */
function needleIdOf(owner: unknown): string | null {
    if (!owner || typeof owner !== "object") return null;
    return getNodeId(owner as Parameters<typeof getNodeId>[0]);
}

export class HierarchyService extends Disposable implements IHierarchyService {
    _serviceBrand: undefined;

    private readonly _onDidChange = new Emitter<HierarchyChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    private readonly _onNodeAdded = new Emitter<IHierarchyNode>();
    readonly onNodeAdded = this._onNodeAdded.event;

    private readonly _onNodeRemoved = new Emitter<IHierarchyNode>();
    readonly onNodeRemoved = this._onNodeRemoved.event;

    private readonly _onNodeChanged = new Emitter<IHierarchyNode>();
    readonly onNodeChanged = this._onNodeChanged.event;

    private readonly _onSelectedNodesDidChange = new Emitter<SelectedNodesChangedEvent>();
    readonly onSelectedNodesDidChange = this._onSelectedNodesDidChange.event;


    constructor(
        @ILogService private readonly logger: ILogService,
        @IInstantiationService private readonly instantiationService: IInstantiationService,
        @ITreeProviderService private readonly treeProviderService: ITreeProviderService,
        @ICommandService private readonly commands: ICommandService,
        @IConfigurationService private readonly configurationService: IConfigurationService,
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
        @IStorageService private readonly storageService: IStorageService,
    ) {
        super();


        this._register(CommandsRegistry.registerCommand(new RenameNodeCommand(this)));
        this._register(CommandsRegistry.registerCommand(new SelectNodeCommand(this, this._onSelectedNodesDidChange)));
        this._register(CommandsRegistry.registerCommand(new DeleteNodeCommand(this, this._onSelectedNodesDidChange)));
        // the SELECTION-driven entry paths behind the global Delete / Ctrl+D
        // keybindings (an object picked in the viewport has no focused tree row)
        this._register(CommandsRegistry.registerCommand(new DeleteSelectedNodesCommand(this)));
        this._register(CommandsRegistry.registerCommand(new DuplicateSelectedNodesCommand(this)));
        this._register(CommandsRegistry.registerCommand(new SetNodeExpandedCommand(this)));
        this._register(CommandsRegistry.registerCommand(new NavigateHierarchyCommand(this, this.logger)));

        const anySelected = contextKeyService.createKey<boolean>("hierarchy.nodeSelected", false);
        this._register(this.onSelectedNodesDidChange(e => {
            anySelected.set(e.selection.length > 0)
        }))


        // Try to restore previous hierarchy state. 
        // We use the first node being added as a signal that the hierarchy is 'ready'
        const restoreDelayer = new ThrottledDelayer<void>(100);
        let disposeTimeout = -1;
        let previousSelected = new WeakSet<IHierarchyNode>();
        const restoreListener = this._register(this.onDidChange((evt) => {

            // Only run restore logic when the hierarchy changes
            if (!evt.hierarchy) return;

            // We use a simple timeout here because otherwise we might run into issues where this never gets disposed
            // E.g. when selected objects change rapidly within one frame
            if (disposeTimeout === -1) {
                disposeTimeout = window.setTimeout(() => {
                    restoreListener.dispose();
                }, 5000); // give the scene 5 seconds to load/stabilize
            }
            restoreDelayer.trigger(async () => {
                runWhenGlobalIdle(() => {
                    const selectedNodes = [];
                    for (const node of this.iterateNodes()) {
                        // Restore state
                        const expanded = SetNodeExpandedCommand.getStorageState(node, this.storageService);
                        if (typeof expanded === "boolean") node.expanded = expanded;
                        // @TODO: ids can change between reloads
                        const selected = SelectNodeCommand.IS_SELECTED(node.id, this.storageService);
                        if (typeof selected === "boolean") {
                            node.selected = selected;
                            if (selected) selectedNodes.push(node);
                        }
                    }
                    if (selectedNodes?.length) {
                        // check if any of the newly selected nodes are actually different from the previous selection (e.g. due to new nodes being added that were previously selected)
                        // We do this extra check here because if an object selected rapidly changes within one frame (every frame) the restoreListener might never dispose and we would end up firing selection changed events indefinitely which can cause performance issues where we accumulate memory over time
                        const selectionChanged = selectedNodes.some(n => !previousSelected.has(n));
                        if (selectionChanged) {
                            previousSelected = new WeakSet(selectedNodes);
                            this._onSelectedNodesDidChange.fire({ selection: selectedNodes });
                            restoreListener.dispose();
                            // Make sure selected nodes are visible by expanding their parents
                            selectedNodes.forEach(node => {
                                let parent = node.parent;
                                while (parent) {
                                    if (!parent.expanded) {
                                        parent.expanded = true;
                                    }
                                    parent = parent.parent;
                                }
                            });
                        }
                    }
                });
            })
        }));
    }

    notifyChanged() {
        this._onDidChange.fire({ hierarchy: this });
    }


    private _selectedCache: IHierarchyNode[] | null = null;
    private _selectedCacheEpoch = -1;
    getSelectedNodes(): IHierarchyNode[] {
        // Called several times per frame from the render loop (selection overlay).
        // Re-walk only when the selection epoch moved (a `selected` flag changed);
        // otherwise return the cached list — O(1) instead of O(scene) per call.
        if (this._selectedCache && this._selectedCacheEpoch === _selectionEpoch) return this._selectedCache;
        const selected: IHierarchyNode[] = [];
        for (const node of this.iterateNodes()) {
            if (node.selected) {
                selected.push(node);
            }
        }
        this._selectedCache = selected;
        this._selectedCacheEpoch = _selectionEpoch;
        return selected;
    }

    *iterateNodes(): Iterable<IHierarchyNode> {
        for (const node of this.roots) {
            if (node) {
                yield* this._traverse(node);
            }
        }
    }
    private * _traverse(node: IHierarchyNode): Iterable<IHierarchyNode> {
        yield node;
        for (const child of node.children) {
            yield* this._traverse(child);
        }
    }

    find(object: object | null | undefined): IHierarchyNode | null {
        if (!object) return null;
        if (this.lookupCacheReference.has(object)) {
            return this.lookupCacheReference.get(object)!;
        }
        return null;
    }

    /**
     * How many objects the hierarchy holds — every registered node, not just the
     * roots. Read on every hierarchy change (the dock tab shows it), so it comes
     * from the id index that registration already maintains rather than a walk:
     * a big scene must not pay for its own size twice.
     */
    get nodeCount(): number {
        return this.lookupCacheId.size;
    }

    findById(id: string): IHierarchyNode | null {
        if (this.lookupCacheId.has(id)) {
            return this.lookupCacheId.get(id)!;
        }
        return null;
    }

    findAllByNeedleId(id: string): IHierarchyNode[] {
        if (!id) return [];
        // verify hits: a holder may have been re-stamped (duplicate healing
        // re-mints ids) or unregistered since it was indexed
        const verified = (): IHierarchyNode[] => {
            const set = this.lookupCacheNeedleId.get(id);
            if (!set) return [];
            const valid: IHierarchyNode[] = [];
            for (const node of set) {
                if (needleIdOf(node.owner) === id && this.lookupCacheReference.get(node.owner) === node) valid.push(node);
                else set.delete(node);
            }
            if (set.size === 0) this.lookupCacheNeedleId.delete(id);
            return valid;
        };
        const hits = verified();
        if (hits.length > 0) return hits;
        // rebuild-on-miss: ids are minted in several places AFTER a node
        // registered (user-create via provenance, save-time stamping) — one
        // walk refreshes every current mapping, then the map answers
        for (const node of this.iterateNodes()) {
            const nodeId = needleIdOf(node.owner);
            if (nodeId) this.indexNeedleId(nodeId, node);
        }
        return verified();
    }

    /** identity is the PAIR (ref, id) — the same asset placed twice
     * legitimately shares baked ids, so the index keeps EVERY holder and
     * consumers disambiguate by scope */
    private indexNeedleId(id: string, node: IHierarchyNode): void {
        let set = this.lookupCacheNeedleId.get(id);
        if (!set) this.lookupCacheNeedleId.set(id, set = new Set());
        set.add(node);
    }

    async search(query: string | string[], maxResults: number = 100, token?: CancellationToken): Promise<IHierarchyNode[]> {
        const results: IHierarchyNode[] = [];

        if (typeof query === "string") {
            query = [query];
        }

        const querySegments = query.map(q => q.toLowerCase().trim());

        const maxPerFrame = 100;
        let frameProcessed = 0;

        for (const node of this.iterateNodes()) {

            if (token?.isCancellationRequested) {
                break;
            }

            if (results.length >= maxResults) {
                break;
            }
            if (++frameProcessed >= maxPerFrame) {
                frameProcessed = 0;
                await new Promise(resolve => setTimeout(resolve, 0));
            }

            let match = false;
            for (const searchTerm of querySegments) {
                if (match) break;
                if (searchTerm === "*" ||
                    node.name.toLowerCase().includes(searchTerm) ||
                    node.searchTags?.some(tag => tag.toLowerCase().includes(searchTerm)) ||
                    node.type.toLowerCase().includes(searchTerm)
                ) {
                    // console.log({ query, searchTerm, name: node.name, tahs: node.searchTags, type: node.type });
                    match = true;
                    results.push(node);
                }
            }
        }
        return results;
    }


    /**
     * The root nodes of the hierarchy.
     */
    readonly roots: IHierarchyNode[] = [];

    private _adding: Map<object, Promise<IHierarchyNode | null>> = new Map();

    async add(object: object, parent?: IHierarchyNode | object, token?: CancellationToken): Promise<IHierarchyNode | null> {

        // if (isHierarchyRootNode(object)) {
        //     this.roots.push(object);
        //     this._onNodeAdded.fire(object.root!);
        //     this._onDidChange.fire({ node: object.root! });
        //     return object.root;
        // }
        // if (isHierarchyNode(object)) {
        //     console.log(object, this.roots
        // }

        if (this._adding.has(object)) {
            const existingPromise = this._adding.get(object)!;
            return existingPromise;
        }

        const promise = this._add(object, token);
        this._adding.set(object, promise);
        try {
            const result = await promise;
            return result;
        } finally {
            this._adding.delete(object);
        }
    }

    private async _add(object: object, token: CancellationToken | undefined): Promise<IHierarchyNode | null> {

        try {

            // check if it already exists
            const existing = this.find(object);
            if (existing) {
                return existing;
            }

            const provider = this.treeProviderService.getProvider(object);
            if (!provider) {
                this.logger.warn(`[HierarchyService] No hierarchy provider found for ${describeObject(object)} — it will not appear in the hierarchy.`);
                return null;
            }
            const rootNode = await this.instantiationService.invokeFunction(a => provider.createTree(a, object));
            if (!rootNode) {
                this.logger.warn("Hierarchy provider failed to create tree");
                return null;
            }

            this._removeRootsInSubtree(rootNode);
            if (!rootNode.parent) this.roots.push(rootNode);
            const node = rootNode;
            const added: IHierarchyNode[] = [];
            this._registerNode(node, added);

            this.logger.debug("HierarchyService: Added object to hierarchy:", object);

            // Fire onNodeAdded for root and ALL children — only the ones that
            // actually entered the hierarchy (see _registerNode)
            for (const n of added) this._onNodeAdded.fire(n);
            this._onDidChange.fire({ hierarchy: this });

            return node;
        } catch (error) {
            this.logger.error("Error adding object to hierarchy:", error);
            return null;
        }
    }

    /**
     * Fire onNodeRemoved event for a node and all its children recursively
     */
    private _fireNodeRemovedRecursive(node: IHierarchyNode): void {
        this._onNodeRemoved.fire(node);
        for (const child of node.children) {
            this._fireNodeRemovedRecursive(child);
        }
    }


    // #region Remove Node
    remove(object: object): void {

        const node = isHierarchyNode(object) ? object : this.find(object);
        if (!node) {
            this.logger.warn("HierarchyService.remove: Object not found in hierarchy:", object);
            return;
        }

        // Check if the node is a root
        let isRoot = false;
        for (let root of this.roots) {
            if (root === node) {
                const index = this.roots.indexOf(root);
                if (index !== -1) {
                    this.roots.splice(index, 1);
                    isRoot = true;
                    break;
                }
            }
        }

        if (node.parent) {
            // removeChild will trigger onDidChange which fires onNodeRemoved via the listener
            node.parent.removeChild(node);
        } else if (isRoot) {
            // Root node without parent - fire events directly since no onDidChange will be triggered
            this._fireNodeRemovedRecursive(node);
            this._unregisterNode(node);
            this._scheduleDisposeIfDetached(node);
        }
        this._pruneSelectionForRemoved(node);

        this._onDidChange.fire({ hierarchy: this });
    }

    /**
     * A removed subtree must LEAVE THE SELECTION — the selection is the source of
     * truth every selection consumer (transform gizmo, selection frame, inspector)
     * reacts to, and without this they kept showing a node that was undone/closed
     * out of the scene (Marcel 2026-07-28: gizmo lingered after undo of an add).
     */
    private _pruneSelectionForRemoved(node: IHierarchyNode): void {
        let pruned = false;
        const visit = (n: IHierarchyNode) => {
            if (n.selected) {
                n.setWithoutNotify("selected", false);
                pruned = true;
            }
            for (const child of n.children) visit(child);
        };
        visit(node);
        if (pruned) this._onSelectedNodesDidChange.fire({ selection: this.getSelectedNodes() });
    }

    clear(): void {
        const hadSelection = this.getSelectedNodes().length > 0;
        for (let i = this.roots.length - 1; i >= 0; i--) {
            const root = this.roots[i];
            // Fire onNodeRemoved for root and all children (before unregistering)
            this._fireNodeRemovedRecursive(root);
            this._unregisterNode(root);
            root.dispose();
            this.roots.splice(i, 1);
        }
        this.roots.length = 0;
        if (hadSelection) this._onSelectedNodesDidChange.fire({ selection: [] });
        this._onDidChange.fire({ hierarchy: this });
    }


    private readonly nodeDisposeables: WeakMap<IHierarchyNode, IDisposable> = new WeakMap();
    private readonly lookupCacheReference: WeakMap<object, IHierarchyNode> = new WeakMap();
    private readonly lookupCacheId: Map<string, IHierarchyNode> = new Map();
    /** needle-id → ALL holder nodes (see {@link findAllByNeedleId}) — fed here,
     * rebuilt on miss; multi-valued because identity is the PAIR (ref, id) and
     * multi-placed assets legitimately share baked ids */
    private readonly lookupCacheNeedleId: Map<string, Set<IHierarchyNode>> = new Map();

    /**
     * Index a node and its subtree. `addedOut` collects the nodes that were NOT
     * known yet — onNodeAdded fires for exactly those. Tree providers reuse
     * existing nodes (ThreeTreeProvider.createNode → internalGetNode), so a
     * later add can hand us a tree whose children are already in the hierarchy;
     * re-announcing them made consumers that read onNodeAdded as a state
     * transition throw (ThreeJs.onAddScene: "Scene already registered").
     * An already-known node still recurses: a re-added tree can carry new
     * descendants, and indexing must not drift from the events.
     */
    private _registerNode(node: IHierarchyNode, addedOut?: IHierarchyNode[]): void {
        if (!this.lookupCacheReference.has(node.owner)) {
            this.lookupCacheReference.set(node.owner, node);
            this.lookupCacheId.set(node.id, node);
            const needleId = needleIdOf(node.owner);
            if (needleId) this.indexNeedleId(needleId, node);
            addedOut?.push(node);

            this.nodeDisposeables.set(node, node.onDidChange(evt => {
                this._onNodeChanged.fire(evt.node);
                if (evt.added) {
                    this._removeRootsInSubtree(evt.added);
                    const added: IHierarchyNode[] = [];
                    this._registerNode(evt.added, added);
                    this._addedBuffer.add(evt.added);
                    this._removedBuffer.delete(evt.added);
                    // Fire onNodeAdded for the added node and all its children
                    for (const n of added) this._onNodeAdded.fire(n);
                    this.evaluateHierarchyStructureChanged();
                }
                if (evt.removed) {
                    // Fire onNodeRemoved for the removed node and all its children (before unregistering)
                    this._fireNodeRemovedRecursive(evt.removed);
                    this._unregisterNode(evt.removed);
                    this._scheduleDisposeIfDetached(evt.removed);
                    this._pruneSelectionForRemoved(evt.removed);
                    this._removedBuffer.add(evt.removed);
                    this._addedBuffer.delete(evt.removed);
                    this.evaluateHierarchyStructureChanged();
                }
            }));
        }

        // register children
        for (const ch of node.children) {
            this._registerNode(ch, addedOut);
        }
    }

    private _removeRootsInSubtree(node: IHierarchyNode): void {
        const index = this.roots.indexOf(node);
        if (index !== -1) this.roots.splice(index, 1);
        for (const child of node.children) this._removeRootsInSubtree(child);
    }
    /**
     * Dispose a removed node once it's clearly not coming back. Removal is not
     * always final — three.js `Object3D.add` fires "removed" then "added"
     * synchronously when reparenting, and drag&drop moves nodes between
     * parents — so disposal is deferred and skipped when the node was
     * re-attached in the meantime. Nodes retained for undo (DeleteNodeCommand
     * re-adds the SAME node instance on undo) are skipped too — disposing them
     * belongs to undo-stack eviction (planned follow-up, see
     * plans/resource-collection.md).
     *
     * Without this, removed nodes were never disposed (only `clear()` disposed
     * roots): tree providers attach polling intervals that stop only on
     * dispose, and a live interval closure is a GC root pinning the node AND
     * its owner object — for meshes that transitively pins geometry, materials
     * and textures of unloaded scenes forever.
     */
    private readonly _pendingNodeDisposal = new WeakMap<IHierarchyNode, number>();

    private _scheduleDisposeIfDetached(node: IHierarchyNode): void {
        const pending = this._pendingNodeDisposal.has(node);
        this._pendingNodeDisposal.set(node, performance.now() + 1000);
        if (pending) return;

        const check = () => {
            const n = node as HierarchyNode;
            const attached = n.parent || this.roots.includes(n) || this.lookupCacheReference.get(n.owner) === n;
            if (n.isDisposed || attached || _undoRetainedNodes.has(n)) {
                this._pendingNodeDisposal.delete(node);
                return;
            }
            // Reparenting reuses the pending check; actual removal still gets a full grace period.
            const remaining = this._pendingNodeDisposal.get(node)! - performance.now();
            if (remaining > 0) {
                setTimeout(check, remaining);
                return;
            }
            this._pendingNodeDisposal.delete(node);
            n.dispose();
        };
        setTimeout(check, 1000);
    }


    private _unregisterNode(node: IHierarchyNode): void {
        if (!this.lookupCacheReference.has(node.owner)) {
            return;
        }
        const obj = node.owner;
        if (this.lookupCacheReference.has(obj)) {
            this.lookupCacheReference.delete(obj);
        }
        if (this.lookupCacheId.has(node.id)) {
            this.lookupCacheId.delete(node.id);
        }
        const needleId = needleIdOf(obj);
        if (needleId) {
            const set = this.lookupCacheNeedleId.get(needleId);
            if (set?.delete(node) && set.size === 0) this.lookupCacheNeedleId.delete(needleId);
        }
        for (const ch of node.children) {
            this._unregisterNode(ch);
        }
        const evts = this.nodeDisposeables.get(node);
        if (evts) {
            evts.dispose();
            this.nodeDisposeables.delete(node);
        }
    }



    private _structureChangedThrottler = new ThrottledDelayer<void>(100);
    private readonly _structureLogCounts = new LRUCache<string, number>(128);
    private readonly _addedBuffer: Set<IHierarchyNode> = new Set();
    private readonly _removedBuffer: Set<IHierarchyNode> = new Set();

    private evaluateHierarchyStructureChanged() {
        if (!this._structureChangedThrottler.isTriggered()) {
            this._structureChangedThrottler.trigger(async () => {
                let anyChanged = this._addedBuffer.size > 0 && Array.from(this._addedBuffer).some(n => !HierarchyNode.hasFlag(n, NodeFlags.HideInHierarchy));
                anyChanged ||= this._removedBuffer.size > 0 && Array.from(this._removedBuffer).some(n => !HierarchyNode.hasFlag(n, NodeFlags.HideInHierarchy));
                if (anyChanged) {
                    // Snapshot identities only: logging live nodes retains their scene resources.
                    const describe = (nodes: Set<IHierarchyNode>) => Array.from(nodes).slice(0, 10).map(node => ({
                        id: node.id, renderKey: node.renderKey, label: node.label, type: node.type,
                    }));
                    // Use every identity, not the truncated log payload; never retain live nodes.
                    const identities = (nodes: Set<IHierarchyNode>) => Array.from(nodes, node => node.renderKey).sort();
                    const signature = JSON.stringify([identities(this._addedBuffer), identities(this._removedBuffer)]);
                    const count = this._structureLogCounts.get(signature) ?? 0;
                    if (count < 11) this._structureLogCounts.set(signature, count + 1);
                    if (count < 10) {
                        this.logger.info("Hierarchy structure changed. Added: " + this._addedBuffer.size + ", Removed: " + this._removedBuffer.size, {
                            added: describe(this._addedBuffer), removed: describe(this._removedBuffer),
                        });
                    } else if (count === 10) {
                        this.logger.info("Further identical hierarchy changes suppressed.", {
                            added: describe(this._addedBuffer), removed: describe(this._removedBuffer),
                        });
                    }
                    this._onDidChange.fire({ hierarchy: this });
                }
                else {
                    this.logger.debug("Hierarchy structure changed, but no visible nodes were added or removed.");
                }
                this._addedBuffer.clear();
                this._removedBuffer.clear();
            });
        }
    }


    // #region Navigate

    /** The tree AS RENDERED: roots in order, expanded children depth-first, hidden
     * nodes (HideInHierarchy) pruned with their subtrees. Up/down arrow navigation is
     * simply +-1 in this list — the previous per-case walk had gaps (never crossed
     * from the last nested node of one root to the next root, went up to a root
     * instead of the previous root's deepest visible node, landed on hidden rows). */
    private visibleRows(): IHierarchyNode[] {
        return flattenVisibleHierarchy(this.roots).map(row => row.node);
    }

    getVisibleRange(from: IHierarchyNode, to: IHierarchyNode): IHierarchyNode[] {
        const rows = this.visibleRows();
        const fromIndex = rows.indexOf(from);
        const toIndex = rows.indexOf(to);
        if (fromIndex < 0 || toIndex < 0) return [];
        return rows.slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1);
    }

    navigate(direction: "up" | "down" | "left" | "right"): IHierarchyNode | null {

        const previousSelected = this.getSelectedNodes();
        const node: IHierarchyNode | null = previousSelected[0] || this.roots[0] || null;
        if (!node) {
            this.logger.debug('No node selected and no roots available for navigation.');
            return null;
        }

        let newNode: IHierarchyNode | null = null;
        switch (direction) {
            case "down":
            case "up": {
                const rows = this.visibleRows();
                const index = rows.indexOf(node);
                if (index < 0) {
                    // current selection is not visible (collapsed away / hidden) —
                    // land on the first row instead of going nowhere
                    newNode = rows[0] ?? null;
                }
                else {
                    newNode = rows[index + (direction === "down" ? 1 : -1)] ?? null;
                }
                break;
            }
            case "right": {
                // standard tree semantics: expand first, enter the first child second
                if (node.hasChildren && !node.expanded) {
                    node.setWithoutNotify("expanded", true);
                    node.notifyChanged();
                }
                else if (node.expanded) {
                    newNode = node.children.find(c => !isHideInHierarchy(c)) ?? null;
                }
                break;
            }
            case "left": {
                // collapse first, go to the parent second
                if (node.expanded && node.hasChildren) {
                    node.setWithoutNotify("expanded", false);
                    node.notifyChanged();
                }
                else if (node.parent && !isHideInHierarchy(node.parent)) {
                    newNode = node.parent;
                }
                break;
            }
        }

        // Set new selection
        if (newNode) {
            previousSelected.forEach(n => {
                n.setWithoutNotify("selected", false);
                n.notifyChanged();
            });

            newNode.setWithoutNotify("selected", true);
            this.logger.debug('New selected node:', newNode.name);

            newNode.notifyChanged();
            this._onSelectedNodesDidChange.fire({ selection: [newNode] });

        }

        return newNode;
    }

}



// #region HierarchyNode




export function isHideInHierarchy(node: unknown): boolean {
    if (!isHierarchyNode(node)) {
        if (typeof node === 'object') {
            node = HierarchyNode.get(node as Record<string | symbol, any>);
            if (!node) return false;
            if (!isHierarchyNode(node)) return false;
        }
        else {
            return false;
        }
    }

    return (node.flags & NodeFlags.HideInHierarchy) !== 0;
}


export const _flagsMap = new WeakMap<object, NodeFlags>();

export class HierarchyNode extends Disposable implements IHierarchyNode {

    /**
     * Gets the HierarchyNode associated with the given object, if any.
     * @param object The object to get the HierarchyNode for.
     * @returns The HierarchyNode or null if none is associated.
     */
    static get(object: object | null | undefined): HierarchyNode | null {
        if (!object) return null;
        if (isHierarchyNode(object)) return object as HierarchyNode;
        return internalGetNode(object);
    }

    // The flags map is the PERSISTENT source of truth per OBJECT; a node mirrors it
    // for fast reads. Nodes are recreated freely (project replace / file close prune
    // subtrees; the tree rebuilds) — flags must survive that, so every mutation writes
    // the map and the node constructor READS without consuming. (The old
    // consume-on-construct made HideInHierarchy objects — the editor grid — pop
    // VISIBLE after their node's first recreation.)
    static setFlags(object: object, flags: NodeFlags): void {
        if (!object) return;
        _flagsMap.set(object, flags);
        const node = HierarchyNode.get(object);
        if (node) node.flags = flags;
    }

    static enableFlag(object: object, flags: NodeFlags): void {
        if (!object) return;
        const node = HierarchyNode.get(object);
        const existingFlags = node?.flags ?? _flagsMap.get(object) ?? NodeFlags.None;
        const combined = existingFlags | flags;
        _flagsMap.set(object, combined);
        if (node) node.flags = combined;
    }
    static disableFlag(object: object, flags: NodeFlags): void {
        if (!object) return;
        const node = HierarchyNode.get(object);
        const existingFlags = node?.flags ?? _flagsMap.get(object) ?? NodeFlags.None;
        const combined = existingFlags & ~flags;
        _flagsMap.set(object, combined);
        if (node) node.flags = combined;
    }
    static hasFlag(object: object | null | undefined, flag: NodeFlags): boolean {
        if (!object) return false;
        const node = HierarchyNode.get(object);
        if (node) {
            return (node.flags & flag) !== 0;
        }
        const existingFlags = _flagsMap.get(object) || NodeFlags.None;
        return (existingFlags & flag) !== 0;
    }



    /** Monotonic instance counter backing {@link renderKey}. */
    private static _renderKeyCounter = 0;

    readonly id: string;
    /** Optional source-object uuid — see IHierarchyNode.uuid. */
    readonly uuid?: string;
    /** Guaranteed-unique keyed-rendering key — see IHierarchyNode.renderKey. */
    readonly renderKey: string;
    readonly children: HierarchyNode[];
    readonly attachments: IAttachment[] = [];

    get hasChildren() {
        return this.children.length > 0 && this.children.some(child => (child.flags & NodeFlags.HideInHierarchy) === 0);
    }

    private _flags: NodeFlags = NodeFlags.None;
    get flags(): NodeFlags { return this._flags; }
    set flags(value: NodeFlags) {
        if (value === this._flags) return;
        this._flags = value;
        this._notifyChanged();
    }


    get parent(): HierarchyNode | null { return this._parent; }


    get owner() {
        return this._source;
    }

    get type() { return this._type; }

    get name(): string { return this._name; }
    set name(value: string) {
        if (value === this._name) return;
        this._name = value;
        this._notifyChanged();
    }

    get label() { return this._label; }
    set label(value: string) {
        if (value === this._label) return;
        this._label = value;
        this._notifyChanged();
    }
    private _label: string = '';

    get selected() { return this._selected; }
    set selected(value: boolean) {
        if (value === this._selected) return;
        this._selected = value;
        _selectionEpoch++; // invalidate HierarchyService.getSelectedNodes()'s cache
    }
    get expanded() { return this._expanded; }
    set expanded(value: boolean) {
        if (value === this._expanded) return;
        this._expanded = value;
        this._notifyChanged();
    }

    get visible() {
        if (this._source && "visible" in this._source) {
            return (this._source as any).visible;
        }
        return true;
    }

    private _icon?: string;
    get icon() {
        return this._icon;
    }
    set icon(value: string | undefined) {
        if (value === this._icon) return;
        this._icon = value;
        this._notifyChanged();
    }

    searchTags: string[] = [];

    setWithoutNotify<T extends keyof Pick<HierarchyNode, "name" | "expanded" | "selected">>(key: T, value: HierarchyNode[T]): void {
        switch (key) {
            case "name":
                this._name = value as string;
                break;
            case "expanded":
                this._expanded = value as boolean;
                break;
            case "selected":
                if (this._selected !== value) {
                    this._selected = value as boolean;
                    _selectionEpoch++; // invalidate HierarchyService.getSelectedNodes()'s cache
                }
                break;
        }
    }

    private readonly _selectionChanged = new Emitter<{ node: HierarchyNode, previous: boolean }>();
    get onSelectionDidChange() { return this._selectionChanged.event; }


    private _notifyChanged(node: HierarchyNode = this) {
        this._didChange.fire({ node });
    }
    notifyChanged = () => this._notifyChanged(this);


    private readonly _didChange = new Emitter<HierarchyNodeChangedEvent>();
    readonly onDidChange = this._didChange.event;

    private readonly _disposed = new Emitter<void>();
    readonly onDidDispose = this._disposed.event;


    private _name: string;
    private readonly _source: object;
    private readonly _type: string;
    private _selected: boolean = false;
    private _parent: HierarchyNode | null = null;
    private _expanded: boolean = true;
    private _visible: boolean = true;

    constructor(init: {
        object: object,
        id: string,
        uuid?: string,
        name: string,
        type?: string,
        parent?: HierarchyNode | null,
        expanded?: boolean,
    },
        // private readonly propertyProvider?: IPropertyService

    ) {
        super();
        this.id = init.id;
        this.uuid = init.uuid;
        this.renderKey = `${init.id}@${++HierarchyNode._renderKeyCounter}`;
        this._name = init.name;
        this._label = init.name;
        this._source = init.object;
        this._expanded = init.expanded ?? true;
        this.children = [];
        this._type = init.type || "unknown";
        this._parent?.addChild(this);

        // read WITHOUT consuming — the map survives node recreation (see setFlags)
        this.flags = _flagsMap.get(init.object) || NodeFlags.None;

        internalAttachNode(this, init.object);
    }

    addChild(child: HierarchyNode, index: number = -1) {
        if (child._parent === this) {
            // the node is already a child
            return;
        }
        // remove from previous parent (if any)
        if (child._parent) {
            child._parent.removeChild(child);
        }
        child._parent = this;
        if (index >= 0 && index < this.children.length) {
            this.children.splice(index, 0, child);
        } else {
            this.children.push(child);
        }
        this._didChange.fire({ node: this, added: child });
    }

    removeChild(child: HierarchyNode) {
        if (child._parent !== this) {
            // not a child of this node
            return;
        }
        child._parent = null;
        const index = this.children.indexOf(child);
        if (index !== -1) {
            this.children.splice(index, 1);
            this._didChange.fire({ node: this, removed: child });
        }
    }

    remove() {
        if (this._parent) {
            this._parent.removeChild(this);
            this._parent = null;
            this._notifyChanged();
        }
    }

    dispose(): void {
        super.dispose();
        for (const child of this.children) {
            child.dispose();
        }
        // Attachments own timers/listeners that capture the three.js object
        // (e.g. HeavyAttachment's vertex-count interval) — without disposing
        // them here, every removed node's object stays pinned by a live timer.
        for (const attachment of this.attachments) {
            (attachment as Partial<IDisposable>).dispose?.();
        }
        this.attachments.length = 0;
        // Detach from the owner object so a later re-add creates a FRESH node
        // (createNode reuses the attached node) instead of resurrecting this
        // disposed one — and so the disposed node doesn't outlive its object.
        internalDetachNode(this, this._source as Record<string | symbol, unknown>);
        this._disposed.fire();
    }

    // @TODO: cache path?
    get path() {
        let parts: string[] = [];
        let current: HierarchyNode | null = this;
        while (current) {
            parts.unshift(current.name || current.type);
            current = current.parent;
        }
        return parts.join("/");
    }

}


/**
 * Nodes an undoable command may re-attach later (DeleteNodeCommand keeps the
 * node instance and re-adds it on undo) — exempt from deferred disposal on
 * removal. Being a WeakSet, entries vanish with the node; actively disposing
 * these when their undo entry is evicted is the planned undo-purge follow-up.
 */
const _undoRetainedNodes = new WeakSet<object>();
export function internalRetainNodeForUndo(node: IHierarchyNode): void {
    _undoRetainedNodes.add(node);
}

const $node = Symbol("needle-editor:node");

export function internalAttachNode(node: HierarchyNode, target: Record<string | symbol, any>) {
    target[$node] = node;
}
export function internalDetachNode(node: HierarchyNode, target: Record<string | symbol, any>) {
    // only detach if the object still points at THIS node (a fresh node may
    // already have been attached for a re-added object)
    if (target && target[$node] === node) {
        delete target[$node];
    }
}
export function internalGetNode(target: Record<string | symbol, any>): HierarchyNode | null {
    return target[$node] || null;
}
